import React, { useState, useRef, useEffect } from 'react';
import { useAuth, Ward } from '@shared/auth/AuthContext';
import { ChevronDown, GraduationCap, Check } from 'lucide-react';

export const ChildSwitcher: React.FC = () => {
  const { role, familyContext, activeStudent, switchActiveStudent } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (role !== 'parent' || !familyContext?.wards || familyContext.wards.length === 0) {
    return null;
  }

  const wards = familyContext.wards;
  const currentWard = activeStudent || wards[0];
  const hasMultiple = wards.length > 1;

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        disabled={!hasMultiple}
        onClick={() => hasMultiple && setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border transition-all ${
          hasMultiple
            ? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20 cursor-pointer shadow-xs'
            : 'bg-muted/40 text-foreground/80 border-border cursor-default'
        }`}
        title={hasMultiple ? 'Click to switch active child view' : 'Active Child Context'}
      >
        <GraduationCap className="w-3.5 h-3.5 text-primary shrink-0" />
        <span className="max-w-[130px] truncate">{currentWard.full_name}</span>
        {currentWard.admission_no && (
          <span className="text-[10px] text-muted-foreground hidden sm:inline">
            ({currentWard.admission_no})
          </span>
        )}
        {hasMultiple && (
          <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && hasMultiple && (
        <div className="absolute left-0 mt-1.5 w-64 rounded-lg bg-card border border-border shadow-lg py-1 z-50 animate-in fade-in-50 zoom-in-95">
          <div className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border/50">
            Select Child View
          </div>
          {wards.map((ward: Ward) => {
            const isSelected = ward.student_id === currentWard.student_id;
            return (
              <button
                key={ward.student_id}
                type="button"
                onClick={() => {
                  switchActiveStudent(ward.student_id);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 hover:bg-accent hover:text-accent-foreground text-xs transition-colors ${
                  isSelected ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
                }`}
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{ward.full_name}</div>
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                    <span>{ward.admission_no || 'ADM-000'}</span>
                    {ward.batch_name && (
                      <>
                        <span>•</span>
                        <span className="truncate">{ward.batch_name}</span>
                      </>
                    )}
                  </div>
                </div>
                {isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
